import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import PropTypes from 'prop-types';

import StorageService from '../../../services/StorageService';
import SessionService from '../../../services/SessionService';
import { Button, Modal, notification } from 'antd';

import { ReactComponent as DelImage } from '../img/del-cross-icon.svg';
import { ReactComponent as UploadImage } from '../img/upload-img-icon.svg';
import { ReactComponent as ArrowLeft } from '../img/arrow-left-icon.svg';
import { ReactComponent as ArrowRight } from '../img/arrow-right-icon.svg';

import { useTranslation } from 'react-i18next';
import Step01Theme from './Step01Theme';
import Step02Details from './Step02Details';
import Step03Ranking from './Step03Ranking';
import { useDispatch, useSelector } from 'react-redux';
import {
  createFinancialStageItem,
  updateFinancialStageItem,
  uploadFinancialItemImage,
} from '../../../data/store/account/accountActions';
import dayjs from 'dayjs';

const ModalCreateItem = ({
  open,
  closeModal,
  financialPlan,
  stageId,
  globalCategory,
}) => {
  const { t } = useTranslation('');
  const dispatch = useDispatch();
  const inputmage = useRef(null);
  const modalRef = useRef(null);

  const [position, setPosition] = useState({ right: 0, top: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const { uploadItemImage = '' } = useSelector((state) => state.accountReducer);

  const [imageUploaded, setImageUploaded] = useState(null);
  const [itemType, setItemType] = useState(null);
  const [stageitem, setStageitem] = useState(null);
  const [navTab, setNavTab] = useState(0);
  
  const [step, setStep] = useState(0);

  const [theme, setTheme] = useState(null);
  const [details, setDetails] = useState({ name: '' });
  const [ranking, setRanking] = useState(null);

  const defaultImage = useMemo(() => {
    if (theme) {
      return `/img/${itemType}/${theme.toLowerCase()}.png`;
    } else {
      return undefined;
    }
  }, [itemType, theme]);

  const handleMouseDown = useCallback((e) => {
    if (
      e.target.tagName === 'INPUT' ||
      e.target.tagName === 'BUTTON' ||
      e.target.tagName === 'TEXTAREA' ||
      e.target.closest('.ant-input') ||
      e.target.closest('.ant-btn')
    ) {
      return;
    }

    if (modalRef.current && modalRef.current.contains(e.target)) {
      setIsDragging(true);
      const rect = modalRef.current.getBoundingClientRect();
      setDragStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      document.body.style.cursor = 'move';
      e.preventDefault();
    }
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging && modalRef.current) {
        const left = e.clientX - dragStart.x;
        const modalWidth = modalRef.current.offsetWidth;
        const right = window.innerWidth - left - modalWidth;
        setPosition({
          right: Math.max(0, right),
          top: Math.max(0, e.clientY - dragStart.y),
        });
      }
    },
    [isDragging, dragStart],
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      document.body.style.cursor = 'default';
    }
  }, [isDragging]);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (open && !isInitialized) {
      setPosition({ right: 0, top: 100 });
      setIsInitialized(true);
    }
  }, [open, isInitialized]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (stageitem) {
      setTheme(stageitem.category);
      const {
        name,
        description,
        amount,
        frequency,
        start_date,
        end_date,
        tags,
      } = stageitem;
      setDetails({
        name,
        description,
        amount,
        frequency,
        start_date: dayjs(start_date),
        end_date: dayjs(end_date),
        tags,
      });
      if (itemType === 'expenses') {
        const { happiness, necessity, purpose } = stageitem;
        setRanking({ happiness, necessity, purpose });
      } else {
        setRanking({ probability: stageitem.probability });
      }
    }
  }, [stageitem]);

  const currentItem = useMemo(() => {
    if (open?.currentItemType) {
      const stage = financialPlan?.stages.find((i) => i.id === +stageId);

      const item = stage?.[open?.currentItemType]?.find(
        (i) => i.id === open.itemId,
      );
      if (item) return { item, type: open.currentItemType };
    }
    return undefined;
  }, [financialPlan, stageId, open]);

  const getStage = useMemo(() => {
    return financialPlan?.stages.find((i) => i.id === +stageId);
  }, [financialPlan, stageId]);

  //TODO check valid reduce for monthly items
  const currentTotal = useMemo(() => {
    const amount = financialPlan?.amount;
    const stage = getStage;
    const totalExp =
      stage?.expenses?.reduce((acc, curr) => acc + curr.amount, 0) ?? 0;
    return amount - totalExp;
  }, [financialPlan, stageId]);

  useEffect(() => {
    if (open) {
      if (open.itemId === 'new') {
        setItemType(open?.currentItemType);
        setStageitem(null);
      } else {
        if (currentItem) {
          setItemType(currentItem.type);
          setStageitem(currentItem.item);
        } else {
          setItemType(null);
          setStageitem(null);
        }
      }
    } else {
      setItemType(null);
      setStageitem(null);
    }
  }, [open, currentItem]);

  const handleClearUpload = () => {
    setImageUploaded(null);
    inputmage.current.value = '';
    dispatch({ type: 'CLEAR_FINANCAL_ITEM_IMAGE' });
  };

  const uploadImage = (event) => {
    const file = event.target.files[0];
    if (file) {
      dispatch(uploadFinancialItemImage(file));
      const objectURL = URL.createObjectURL(file);
      setImageUploaded(objectURL);
    }
  };

  // eslint-disable-next-line no-undef
  const baseURL = process.env.REACT_APP_BASE_URL || '';
  const token = StorageService.getJWTToken() || SessionService.getJWTToken();
  const imagePath = `${baseURL}/v1/financialplan/image/${currentItem?.item?.image}`;

  useEffect(() => {
    if (stageitem?.image) {
      fetch(imagePath, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.blob();
        })
        .then((blob) => {
          const objectURL = URL.createObjectURL(blob);
          setImageUploaded(objectURL);
        })
        .catch((error) => {
          console.error(
            'There was a problem with your fetch operation:',
            error,
          );
        });
    }
  }, [stageitem]);


  const category = open?.globalCategory || globalCategory;

  const stepData = {
    0: [''],
    1: [
      'GIVE_IT_A_NAME',
      'WHAT_SORT_OF_BUDGET_ARE_YOU_THINKING',
      category === 'giving_legacy'
        ? 'WHEN_DO_YOU_WANT_TO_SPEND_ON_THIS'
        : 'HOW_OFTEN_DO_YOU_WANT_TO_DO_IT',
      'WHEN_DO_YOU_USUALLY_PAY_FOR_THIS',
    ],
    2:
      itemType === 'expenses'
        ? category === 'daily_life'
          ? ['HOW_DOES_THIS_ALIGN_WITH_YOUR_NEEDS']
          : category === 'lifestyle_experiences'
            ? [
                'HOW_DOES_THIS_ALIGN_WITH_MY_PURPOSE',
                'HOW_HAPPY_DOES_IT_MAKE_YOU',
              ]
            : ['HOW_DOES_THIS_ALIGN_WITH_MY_PURPOSE', 'HOW_ESSENTIAL_IS_THIS']
        : [''],
  };

  const previousStep = () => {
    if (step === 0 && navTab === 0) {
      return;
    } else if (step === 0 && navTab !== 0) {
      setNavTab(navTab - 1);
      setStep(0);
    } else {
      setStep(step - 1);
    }
  };

  const nextStep = () => {
    if (step === stepData?.[navTab].length - 1) {
      if (navTab === 2) return;
      setNavTab(navTab + 1);
      setStep(0);
    } else {
      setStep(step + 1);
    }
  };

  const handleCloseModal = () => {
    setStep(0);
    setNavTab(0);
    setTheme(null);
    setDetails({ name: '' });
    setRanking(null);
    setImageUploaded(null);
    setIsDragging(false);
    setIsInitialized(false);
    setStageitem(null);
    setItemType(null);
    document.body.style.cursor = 'default';
    if (modalRef.current) {
      modalRef.current.removeEventListener('mousedown', handleMouseDown);
    }
    closeModal();
  };

  const validateForm = () => {
    const errors = [];

    if (!globalCategory && !open?.globalCategory) {
      errors.push('global_category');
    }
    if (!theme) {
      errors.push('category');
    }
    if (!details?.name) {
      errors.push('name');
    }
    if (!details?.amount) {
      errors.push('amount');
    }
    if (details?.frequency === undefined || details?.frequency === null) {
      errors.push('frequency');
    }
    if (!details?.start_date) {
      errors.push('start_date');
    }
    if (details?.frequency !== 0 && !details?.end_date) {
      errors.push('end_date');
    }

    if (details?.start_date && details?.end_date && details?.frequency !== 0) {
      const startDate = dayjs(details.start_date);
      const endDate = dayjs(details.end_date);
      if (endDate.isBefore(startDate) || endDate.isSame(startDate)) {
        errors.push(t('END_DATE_AFTER_START'));
      }
    }

    if (itemType === 'incomes') {
      if (ranking?.probability === undefined || ranking?.probability === null) {
        errors.push('probability');
      }
    }

    return errors;
  };

  const handleSubmite = () => {
    const validationErrors = validateForm();

    if (validationErrors.length > 0) {
      notification.error({
        message: (
          <h1 className="notification-title">{t('VALIDATION_ERROR')}</h1>
        ),
        description: (
          <p className="notification-description">
            {t('VALIDATION_ERROR_TEXT')} {validationErrors.join(', ')}
          </p>
        ),
        placement: 'bottomRight',
        duration: 4,
        className: 'notification-error',
      });
      return;
    }

    if (open.itemId === 'new') {
      const data = {
        callback: handleCloseModal,
        stageId,
        itemType,
        data: {
          global_category: open.globalCategory || globalCategory,
          image: uploadItemImage,
          category: theme,
          description: '',
          ...details,
          end_date:
            details.frequency === 0 ? details.start_date : details.end_date,
          ...ranking,
        },
      };
      dispatch(createFinancialStageItem(data));
    } else {
      let newDate = {};
      newDate.start_date =
        typeof details.start_date !== 'string'
          ? dayjs(details.start_date).format('YYYY-MM-DD')
          : details.start_date;

      newDate.end_date =
        details?.end_date && typeof details.end_date !== 'string'
          ? dayjs(details.end_date).format('YYYY-MM-DD')
          : (details?.end_date ?? newDate.start_date);

      const data = {
        callback: handleCloseModal,
        id: stageitem.id,
        stageId,
        itemType,
        data: {
          global_category: globalCategory || stageitem?.image,
          image: uploadItemImage,
          category: theme,
          description: '',
          ...details,
          ...ranking,
          ...newDate,
        },
      };
      dispatch(updateFinancialStageItem(data));
    }
  };

  return (
    <Modal
      width={1120}
      className="modal-content-paper stage-item"
      open={open}
      onCancel={handleCloseModal}
      footer={null}
      zIndex={1000}
      destroyOnClose
      maskClosable={false}
      style={{
        position: 'fixed',
        right: position.right,
        height: 'auto',
        top: position.top,
        cursor: isDragging ? 'move' : 'default',
      }}
      getContainer={() => {
        setTimeout(() => {
          const modalElement = document.querySelector(
            '.ant-modal.modal-content-paper.stage-item',
          );
          if (modalElement && modalRef.current !== modalElement) {
            modalRef.current = modalElement;
            modalElement.addEventListener('mousedown', handleMouseDown);
          }
        }, 0);
        return document.body;
      }}
    >
      <div className="stageitem-creator-wrapper">
        <div className="stageitem-creator-leftside">
          {itemType === 'expenses' && (
            <div className="image-upload-wrapper">
              <input
                ref={inputmage}
                type="file"
                accept={'image/*'}
                className="item-img-upload"
                onChange={uploadImage}
              />
              <Button
                type="text"
                className="finplan-item-add-btn"
                onClick={() => inputmage.current.click()}
                icon={<UploadImage />}
                iconPosition={'end'}
              >
                {t('UPLOAD')}
              </Button>
              {imageUploaded && (
                <div className="name-upload-wrapper">
                  <DelImage
                    className="clear-uploaded"
                    onClick={handleClearUpload}
                  />
                </div>
              )}
            </div>
          )}
          <div className="stageitem-creator-img">
            <img
              src={imageUploaded ? imageUploaded : defaultImage}
              alt="expenses img"
              className=""
            />
          </div>
        </div>
        <div className="stageitem-creator-rightside">
          {navTab === 2 && (
            <Button
              type="text"
              onClick={handleSubmite}
              className="stageitem-confirm-btn"
            >
              {t('CONFIRM')}
            </Button>
          )}
          <div className="stageitem-navigation-wrapper">
            <div
              onClick={() => {
                setNavTab(0);
                setStep(0);
              }}
              className={`stageitem-navigation-btn ${navTab === 0 ? 'selected' : ''}`}
            >
              {t('THEME')}
            </div>
            <div
              onClick={() => {
                setNavTab(1);
                setStep(0);
              }}
              className={`stageitem-navigation-btn ${navTab === 1 ? 'selected' : ''}`}
            >
              {t('DETAILS')}
            </div>
            <div
              onClick={() => {
                setNavTab(2);
                setStep(0);
              }}
              className={`stageitem-navigation-btn ${navTab === 2 ? 'selected' : ''}`}
            >
              {t('RANKING')}
            </div>
          </div>
          {navTab === 0 && (
            <Step01Theme
              theme={theme}
              setTheme={setTheme}
              itemType={itemType}
              globalCategory={open?.globalCategory || globalCategory}
              t={t}
            />
          )}
          {navTab === 1 && (
            <Step02Details
              details={details}
              setDetails={setDetails}
              currentTotal={currentTotal}
              itemType={itemType}
              step={step}
              stepData={stepData[1]}
              stage={getStage}
            />
          )}
          {navTab === 2 && (
            <Step03Ranking
              ranking={ranking}
              setRanking={setRanking}
              itemType={itemType}
              stepData={stepData[2]}
              step={step}
              globalCategory={open?.globalCategory || globalCategory}
            />
          )}
          <div className="stageitem-point-footer">
            <Button
              onClick={previousStep}
              style={{ cursor: 'pointer' }}
              icon={<ArrowLeft className="btn-finplan-arrow" />}
              className="btn-finplan-button"
            >
              {t('PREV')}
            </Button>
            <div className="dot-point-wrapper">
              {stepData?.[navTab].map((i, index) => {
                return (
                  <div
                    key={index}
                    className={`dot-point ${step === index ? 'selected' : ''}`}
                    onClick={() => setStep(index)}
                  />
                );
              })}
            </div>
            {navTab === 2 && step === stepData?.[navTab].length - 1 ? (
              <div></div>
            ) : (
              <Button
                onClick={nextStep}
                style={{ cursor: 'pointer' }}
                icon={<ArrowRight className="btn-finplan-arrow" />}
                iconPosition="end"
                className="btn-finplan-button"
              >
                {t('NEXT')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalCreateItem;

ModalCreateItem.propTypes = {
  open: PropTypes.object,
  closeModal: PropTypes.func,
  financialPlan: PropTypes.object,
  stageId: PropTypes.string,
  viewType: PropTypes.string,
  globalCategory: PropTypes.string,
};
